package it.unibo.tuprolog.ui.gui.prolog

import it.unibo.tuprolog.core.operators.OperatorSet
import it.unibo.tuprolog.solve.Solution
import it.unibo.tuprolog.solve.SolverFactory
import it.unibo.tuprolog.solve.library.Library
import it.unibo.tuprolog.solve.libs.io.IOLib
import it.unibo.tuprolog.solve.libs.oop.OOPLib
import it.unibo.tuprolog.ui.gui.identity.FeatureId
import it.unibo.tuprolog.ui.gui.identity.SolverProfileId
import it.unibo.tuprolog.ui.gui.model.FeatureValue
import it.unibo.tuprolog.ui.gui.presentation.OperatorPresentation
import it.unibo.tuprolog.ui.gui.solver.SolverCapabilities
import it.unibo.tuprolog.ui.gui.solver.SolverProfile
import it.unibo.tuprolog.ui.gui.solver.SolverSessionCreationRequest

private val INSPECTION_CAPABILITIES =
    setOf(
        SolverCapabilities.STATIC_KB_INSPECTION,
        SolverCapabilities.DYNAMIC_KB_INSPECTION,
        SolverCapabilities.OPERATORS_INSPECTION,
        SolverCapabilities.FLAGS_INSPECTION,
        SolverCapabilities.LIBRARIES_INSPECTION,
        SolverCapabilities.INTERACTIVE_INPUT,
    )

/**
 * Builds a [SolverProfile] backed by any [SolverFactory] (prolog, ProbLog, ...); no toolkit dependency.
 *
 * [runtimeLibraries] defaults to [OOPLib] and [IOLib], matching every existing caller (e.g. ide-swing). Pass a
 * narrower list on platforms where a default library doesn't work: [OOPLib] is reflection-based and throws
 * [NotImplementedError] on Kotlin/JS the moment a runtime tries to use it, which previously failed every single
 * resolution in ide-web regardless of whether the query needed OOP features at all.
 */
@Suppress("LongParameterList")
fun solverFactoryProfile(
    factory: SolverFactory,
    id: SolverProfileId,
    displayName: String,
    capabilities: Set<String> = setOf(SolverCapabilities.CANCELLATION),
    solutionFeatures: (Solution) -> Map<FeatureId, Map<String, FeatureValue>> = { emptyMap() },
    runtimeLibraries: List<Library> = listOf(OOPLib, IOLib),
    // Lets a frontend-specific module (e.g. gui-plp) pick a SolverFactorySession subclass instead of
    // duplicating this function's capability-merging/operator-computation logic.
    newSession: (
        factory: SolverFactory,
        request: SolverSessionCreationRequest,
        capabilities: Set<String>,
        solutionFeatures: (Solution) -> Map<FeatureId, Map<String, FeatureValue>>,
        runtimeLibraries: List<Library>,
    ) -> SolverFactorySession = ::SolverFactorySession,
): SolverProfile =
    SolverProfile(
        id = id,
        displayName = displayName,
        capabilities = SolverCapabilities(capabilities + INSPECTION_CAPABILITIES),
        factory = { request ->
            newSession(factory, request, capabilities + INSPECTION_CAPABILITIES, solutionFeatures, runtimeLibraries)
        },
        // Cheap and pure (no solver instantiated): a dialect's own operators (e.g. ProbLog's `::`) come from
        // its defaultBuiltins library, same as a freshly built solver would see before any custom `:- op(...)`.
        defaultOperators =
            (OperatorSet.DEFAULT + factory.defaultBuiltins.operators).map {
                OperatorPresentation(it.functor, it.priority, it.specifier.name)
            },
    )
